/**
 * DnaGlyph.tsx — CANON SHIM (locked 2026-05-27 per CANON_VISUALS_LOCKED.md §3)
 *
 * The legacy DnaGlyph double-helix icon is dead. This module now renders
 * TowerMiniIcon (tower-with-DNA-helix-inside) so all six legacy callsites
 * pick up the canon glyph automatically without per-callsite rewrites.
 *
 * Callsites kept working (TopNav, Coaching, ManagerHub, HelixDashboard,
 * DNAPage, AccountList) — they still pass {score, size, ariaLabel}.
 *
 * Do not reintroduce the old double-helix SVG. To change the glyph,
 * edit TowerMiniIcon directly.
 */

import React from "react";
import TowerMiniIcon from "@/components/helix/TowerMiniIcon";
import { getHealthColor, HEALTH_PALETTE } from "@shared/schema";

// Legacy helper kept for any code still importing it (Likert 0..4 → canon hex).
// Canon palette: gray / red / orange / yellow / green.
export function getScoreColor(pts: number | null | undefined): string {
  if (pts == null) return HEALTH_PALETTE.gray;
  if (pts >= 4) return HEALTH_PALETTE.green;
  if (pts >= 3) return HEALTH_PALETTE.green;
  if (pts >= 2) return HEALTH_PALETTE.yellow;
  if (pts >= 1) return HEALTH_PALETTE.orange;
  return HEALTH_PALETTE.red;
}

// Composite bucket — 0..100 score routed through canon palette.
export function getCompositeBucketColor(score: number | null | undefined): string {
  if (score == null) return HEALTH_PALETTE.gray;
  return getHealthColor(score);
}

export interface DnaGlyphProps {
  score?: number | null;
  cpScores?: number[] | null;
  size?: 16 | 20 | 24 | 32 | 48;
  ariaLabel?: string;
  className?: string;
}

// Map size to a tower mini-icon width/height while keeping the ~2:3 ratio.
function towerDimsForSize(size: number): { width: number; height: number } {
  // Old DnaGlyph was square (size x size). TowerMiniIcon is 2:3.
  // Use the requested 'size' as the height anchor and scale width by 2/3.
  const height = size;
  const width = Math.max(12, Math.round((size * 2) / 3));
  return { width, height };
}

export function DnaGlyph({
  score = null,
  size = 24,
  ariaLabel,
  className,
}: DnaGlyphProps) {
  const { width, height } = towerDimsForSize(size);
  const composite = score ?? 0;
  return (
    <span
      className={className}
      style={{ display: "inline-flex", alignItems: "center", lineHeight: 0 }}
    >
      <TowerMiniIcon
        composite={composite}
        width={width}
        height={height}
        ariaLabel={ariaLabel}
      />
    </span>
  );
}

export default DnaGlyph;
